<template lang="pug">
  .boost-module(v-if="showBoostModule")
    transition(name="height")
      .boost-module-wrap(v-if="!maxBoostsAdded")
        .boost-added-message.absolute-wrap.text-center.bg-gray-light(v-if="boostAdded")
          .v-center.full-width
            span.icon-check.text-color-2.xs-f-xlarge.max-at-tweak
            p.xs-f-small.max-at-tweak(role="alert") Your item was added to bag

        .boost-module-cta.xs-12(:class="{'boost-added': boostAdded}")
          .boost-intro.xs-12.text-center
            p.xs-f-medium.lg-f-xmedium.max-at-tweak(v-html="introText")

          .boost-product-info.xs-12
            button.btn-reset.checkbox-wrap.full-width(@click="() => addBoost()")
              .mr-checkbox(:class="{selected: boostChecked}")
              span.ada-tooltip Click to add a pro boost to your bag”
              img.boost-image(:src="boostImageObj.url" :alt="boostImageObj.alt_text")
              .boost-info
                span.boost-name.upper.xs-f-xsmall.max-at-tweak {{ boostToOffer.name }}
                span.boost-price.xs-f-small.lg-f-medium.max-at-tweak {{ $currencyShort(boostToOffer.price) }}

          .boost-ctas.xs-12
            button.btn-reset.pull-left.brand-color-1.xs-f-small.max-at-tweak( v-if="!isExperimentOnlySealEnabled" @click="showBoostModal") More boosts
              span.ada-tooltip Click to see more boost options
            button.btn-reset.pull-right.brand-color-1.xs-f-small.max-at-tweak(@click="showBoostModal") What's a boost?
              span.ada-tooltip Click to learn more about boosts
          MrSpinnerVeil(v-if="addingBoost")

</template>

<script>
  import { mapState, mapActions, mapMutations } from 'vuex';
  import boostMixin from '@mixins/boostMixin';
  import MrSpinnerVeil from '@components/MrSpinnerVeil';

  export default {
    name: 'BoostModule',

    components: {
      MrSpinnerVeil,
    },

    props: {
      trackEventTitle: {
        type: String,
        default: 'Boost - Added'
      }
    },

    mixins: [boostMixin],
    inject: ['getObjProperty'],

    data() {
      return {
        boostChecked: false,
        boostAdded: false,
        boostsAdded: [],
        addingBoost: false,
        sealExperimentAddedToBag: false,
        experimentName: 'Pro Boost Seal on RCC',
      };
    },

    computed: {
      ...mapState('pdp', ['version', 'product']),
      ...mapState('cart', ['addingProduct']),
      ...mapState('xsell', ['proBoostSeal']),
      ...mapState('xsell', ['proBoostSeal']),

      useProBoostSealExperiment() {
        return this.isExperimentOnlySealEnabled && !this.sealExperimentAddedToBag;
      },

      boostToOffer() {
        if (this.useProBoostSealExperiment) {
          return this.proBoostSeal;
        }
        return this.selectedBoost;
      },

      isExperimentOnlySealEnabled() {
        return this.experiments[this.experimentName] === 'B';
      },

      showBoostModule() {
        return this.boostToOffer && this.boostToOffer.id && !this.maxBoostsAdded;
      },

      introText() {
        if (this.useProBoostSealExperiment) {
          return "<span class='bold'>Want longer lasting color?</span> —add a hair treatment to <span class='bold'>lock in your color<b/>";
        }
        return this.selectedBoost.recommended
          ? `You mentioned wanting ${this.selectedBoost.wantText}&mdash;add a Pro&nbsp;Boost`
          : `Want ${this.selectedBoost.wantText}?&mdash;add a Pro&nbsp;Boost`;
      },

      boostImageObj() {
        return this.getObjProperty(this.boostToOffer, 'imagery.thumb') || {};
      },

      maxBoostsAdded() {
        return this.boostsAdded.length >= 2 || this.boostsAdded.length >= this.boostRecommendations.length;
      },
    },

    methods: {
      ...mapActions('modal', ['showModal']),
      ...mapActions('cart', ['addToCart']),
      ...mapMutations('pdp', ['setAttachableProdId']),

      showBoostModal() {
        this.showModal({
          component: 'BoostModal',
          props: {
            addBoost: this.addBoost,
            showOnlySeal: this.useProBoostSealExperiment,
          },
          theme: 'no-padding',
        });
      },

      async addBoost() {
        if (this.boostChecked) {
          return;
        }
        this.addingBoost = true;
        this.boostChecked = true;

        const payload = {
          productId: this.boostToOffer.id,
          ctaSource: 'boost-module',
          cartType: 'active',
        };

        return new Promise((resolve, reject) => {
          this.addToCart(payload).then(() => {
            this.boostAdded = true;
            this.setAttachableProdId(this.boostToOffer.id);

            this.trackMREvent(this.trackEventTitle, {
              productId: this.boostToOffer.id,
            });

            setTimeout(() => {
              this.boostChecked = false;
              this.boostAdded = false;
              this.addingBoost = false;
              this.boostsAdded.push(payload.productId);

              let nextRecIndex = null;

              for (let x = 0; x < this.boostRecommendations.length; x++) {
                if (nextRecIndex === null && !this.boostsAdded.includes(this.boostRecommendations[x].id)) {
                  nextRecIndex = x;
                  break;
                }
              }

              if (nextRecIndex != null) {
                this.updateSelectedBoost(nextRecIndex);
                if (this.isExperimentOnlySealEnabled) {
                  this.sealExperimentAddedToBag = true;
                }
              }
              resolve();
            }, 1500);
          }).catch((error) => {
            this.boostChecked = false;
            this.boostAdded = false;
            this.addingBoost = false;
            reject(error || new Error('Failed to add boost to cart'));
          });
        });
      },
    },

    mounted() {
      if (this.experiments[this.experimentName]) {
        this.mix_trackExperimentViewed(this.experimentName);
      }
    }
  };
</script>

<style scoped lang="stylus">
  .boost-module
    .boost-module-wrap
      max-height 22em
      overflow hidden
      position relative

      .boost-added-message
        margin auto
        opacity 100
        top 1em
        border-radius 0.5em

        p
          margin-top 1.5em

      .boost-module-cta
        opacity 100
        transition(opacity 300ms ease-in-out)
        background-color color-white
        border 1px solid ui-color-5
        border-radius 0.5em
        margin-top 1em

        &.boost-added
          opacity 0
          pointer-events none

        .boost-intro
          padding 1em 5%
          border-bottom 1px solid ui-color-5

        .boost-product-info
          padding 1.5em 5%

          .boost-label
            display block

          .mr-checkbox
          .boost-image
          .boost-info
            display inline-block
            vertical-align top

          .mr-checkbox
            margin-top 2em
            margin-right 1em

          .boost-image
            width 5.5em
            margin-right 1em

          .boost-info
            width 9em
            text-align left

            span
              display block

        .boost-ctas
          padding 1em 5%
          padding-top 0

          button
            text-decoration underline

      .boost-module-cta-b
        opacity 100
        transition(opacity 300ms ease-in-out)

        &.boost-added
          opacity 0
          pointer-events none

      @media mq-mobile
        padding 0 5%

        .boost-added-message
          width 90%

        .boost-module-cta
          .boost-product-info
            .boost-info
              width 8em

      @media mq-tablet
        padding 0 10%

        .boost-added-message
          width 80%

        .boost-module-cta
          .boost-product-info
            .boost-info
              width 9em

    .height-enter-active
    .height-leave-active
      transition(max-height 500ms ease-in-out)

    .height-enter
    .height-leave-to
      max-height 0

    &.version-b
      padding 1.5em 0
      padding-bottom 0

      .boost-product-info
        padding 0.5em 0

      .add-button
        position relative
        display flex
        flex-direction row
        justify-content stretch
        align-items center
        background color-white
        text-align left
        width 100%
        border 1px solid ui-color-5

        .boost-image
          width 4em
          border-right 1px solid ui-color-5

        .boost-info
          width 100%
          padding-left 1em

          .boost-name
            width 100%

        .icon-pos-full-container
          width 4em
          display flex
          flex-direction row
          align-items center
          justify-content center
          .icon-pos-full
            padding 0.5em

      .learn-more
        color text-color-4

      @media mq-tablet-less
        padding-bottom 1.5em
        background ui-color-3
</style>
